/**
 * Build-time feature flags. Originally the #137 smart-wallet work (Phase 1 + Phase 1b); now also
 * the #261 F7 native-source Max gas reserve. Every constraint below applies to EVERY flag added
 * here, not only the smart-wallet pair.
 *
 * ── THIS FILE MUST HAVE ZERO IMPORTS. THAT IS AN ACCEPTANCE CRITERION, NOT A STYLE RULE. ─────────
 * `wagmi.ts` imports this module at module-init scope to build the Coinbase connector. Meanwhile
 * `smartAccount.ts:27` imports `wagmiConfig` from `@/config/wagmi`. So the moment this file imports
 * anything that (transitively) reaches `smartAccount.ts`, the cycle
 *
 *     wagmi → flags → smartAccount → wagmi
 *
 * closes while `wagmiConfig` is still a `const` in its temporal dead zone, and the app dies at import
 * with a ReferenceError before a single component renders.
 *
 * The tempting future edit is to "de-duplicate" `isSmartWalletRoutingEnabled()` below by importing it
 * from `@/utils/smartAccount` instead of re-reading the env var. DO NOT. The duplicate read is
 * deliberate and load-bearing. Both readers compare the same variable to the same literal, and the
 * `flags.test.ts` T4 case asserts on this file's own source text so that adding an import fails the
 * suite rather than merely looking wrong in review.
 *
 * ── WHY FUNCTIONS AND NOT MODULE-SCOPE CONSTS ────────────────────────────────────────────────────
 * A `const` would freeze at first import. There is no `vi.resetModules()` anywhere in `apps/web`
 * (verified: 0 occurrences), and the flag tests mutate `import.meta.env` in `beforeEach` — so every
 * test after the first would assert against a stale snapshot, and the gate would be UNTESTED while
 * appearing fully covered. Reading inside the function body is what makes the tests real.
 * `smartAccount.ts:75` already does this correctly; this matches that shape.
 *
 * ── PRODUCTION STATE (updated 2026-07-20): BOTH FLAGS ARE ON IN PROD. ────────────────────────────
 * As of 2026-07-20 both `VITE_SMART_WALLET_ENABLED` and `VITE_SMART_WALLET_OFFER_AT_CONNECT` are in
 * the ALLOWED list in `scripts/deploy-prod-fe.sh` and set to `'true'` as repo variables, so both
 * `isSmartWalletRoutingEnabled()` and `isSmartWalletOfferEnabled()` return true in the prod bundle
 * (verified in the served bytes of `app.parsedefi.com`: `preference:"all"` present, `eoaOnly` absent).
 *
 * ⚠️ This paragraph previously asserted "the production gate is OMISSION … Neither name below is on
 * that list … both comparisons are false." THAT IS NO LONGER TRUE and was actively misleading:
 * unlike the deploy files, this prose is not pinned by `deployAllowlist.test.ts` (that test reads the
 * deploy files' source text, not this comment), so it went stale silently on launch and misled TWO
 * separate agents this session into reporting prod was still `eoaOnly`. If you are gating a FUTURE
 * flag by omission, the mechanism described still works (clean worktree + ALLOWED filter + Vite
 * inlining `undefined`) — but do not read it as a statement about THESE two flags' current state.
 */

/**
 * Phase 1 ROUTING: detect a smart-contract account and send it to `bridge()` instead of the
 * EIP-3009 `bridgeWithAuth()` it cannot sign.
 *
 * Deliberately duplicates `isSmartWalletRoutingEnabled()` in `@/utils/smartAccount` rather than
 * importing it — see the zero-imports note above.
 */
export function isSmartWalletRoutingEnabled(): boolean {
  return import.meta.env.VITE_SMART_WALLET_ENABLED === 'true'
}

/**
 * Phase 1b OFFER: let Coinbase offer the Base Account (passkey) option at connect time by passing
 * `preference: 'all'` instead of `'eoaOnly'`.
 *
 * ── THE AND-GUARD IS THE ENTIRE POINT OF THIS FUNCTION ───────────────────────────────────────────
 * Offering smart wallets while routing is off is not a degraded state, it is a fund-flow trap: the UI
 * hands a user an account type it then cannot bridge with. That exact combination shipped for 27 days
 * (`e3cbdd5` → `3b2f446`, 2026-05-07) and is what created the R12 cohort — users holding USDC in a
 * Base Account that reverts on `receiveWithAuthorization` because Circle's `SignatureChecker` has no
 * ERC-6492 branch.
 *
 * So the state table has three reachable rows and one that must be structurally impossible:
 *
 *     routing off / offer off   today's production                        SAFE
 *     routing ON  / offer off   beta today; serves the R12 cohort         SAFE
 *     routing ON  / offer ON    full Phase 1 on beta                      GOAL
 *     routing off / offer ON    reproduces 2026-05-07             UNREACHABLE
 *
 * Expressed as a single `&&` over the routing READ, not as two independent flag reads that a later
 * refactor could split apart. `flags.test.ts` T1 pins the unreachable row for every value of
 * `VITE_SMART_WALLET_OFFER_AT_CONNECT`, including `'true'`.
 */
export function isSmartWalletOfferEnabled(): boolean {
  return (
    import.meta.env.VITE_SMART_WALLET_ENABLED === 'true' &&
    import.meta.env.VITE_SMART_WALLET_OFFER_AT_CONNECT === 'true'
  )
}

/**
 * #261 F7 — native-source "Max" gas reserve.
 *
 * OFF (default, and today's production behaviour): `maxSendReserve` reserves NOTHING on a native
 * source, so Max fills the full balance and the native-fee gate then blocks it unconditionally —
 * `balance >= youPaySource + gas*0.7` where `youPaySource = input + fixFee`, i.e.
 * `B >= B + fixFee + 0.7g`, which is false for any g > 0. Max is structurally dead on natives.
 * A cross-chain STABLE source meanwhile reserves `opexUsd`.
 *
 * ON: the reserve becomes the ON-TOP NATIVE costs only — padded gas + the deBridge flat fixFee —
 * and a stable source reserves NOTHING (its Max is the full balance; under fixed-input
 * `give == input` the opex comes out of the RECEIVE, not the send, so reserving it from the send
 * both understates Max and double-counts). `opexGate` is realigned to the same `give == input`
 * model in the same flag branch: the two are COUPLED, because setting the stable reserve to 0
 * without realigning the gate makes a full-balance Max trip `tokenBal < input + opexUsd`.
 *
 * Beta-only (BETA_ONLY_FLAGS in deployAllowlist.test.ts); withheld from production. The production
 * gate is OMISSION from ALLOWED in scripts/deploy-prod-fe.sh — do not add it there without a
 * separate decision, that edit IS the launch.
 */
export function isNativeMaxGasReserveEnabled(): boolean {
  return import.meta.env.VITE_NATIVE_MAX_GAS_RESERVE === 'true'
}

/**
 * #185 / #614 — the native-source 1Click deposit arms (SOL / TRX / EVM-native). Read through an
 * accessor like every sibling flag; the hook's own raw read stays as the second line behind the
 * pre-sign gate in BridgePage. Beta-only (BETA_ONLY_FLAGS); withheld from production.
 */
export function isNativeOneclickDepositEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_NATIVE_1CLICK_DEPOSIT === 'true'
}

/**
 * #614 Phase A — beta-first preview of Tron same-chain TRX↔USDT through 1Click. When 'true', the DEX
 * API client attaches `routingPreview=tron_samechain_1click` to every tron→tron estimate /
 * reverse-estimate / bridge call; the API, behind its own SSM switch, treats that request as
 * `near_1click` when the persisted row is still deBridge. The production bundle never carries this
 * flag, so production requests never carry the opt-in. It is a preview switch, not a security
 * boundary. Beta-only (BETA_ONLY_FLAGS); withheld from production. Becomes a no-op once the operator
 * flips the rows (Phase B).
 */
export function isTronSamechainOneclickPreviewEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_TRON_SAMECHAIN_1CLICK === 'true'
}

/**
 * #261 F9 — per-step failure state (same-chain), drawer template branch, one terminal title, and the
 * per-mount footer label.
 *
 * OFF (default, and today's production behaviour): a failed same-chain swap flips ALL THREE step rows
 * to "Failed" (`steps.ts` short-circuits every phase once `dexStep === 'error'`), the same swap
 * reopened from Activity renders the CROSS-CHAIN template ("Moving between chains" / "Delivering to
 * …") because the drawer panel never branches on same-chain, the three mounts carry three different
 * terminal titles, and the error footer's button reads "Cancel" while its handler is `onStartNew`.
 *
 * ON: only the step that actually failed reads as failed, the drawer renders the same-chain template,
 * one helper produces the terminal title at every mount for failure AND success, and the footer button
 * is labelled per mount ("Close" where it only dismisses, "Start over" where it really resets).
 *
 * This flag gates RENDERING ONLY. The `failedStep` CAPTURE (useDexBridge / reconcileDexOrders /
 * dexOrderStorage) is deliberately UNGATED, so a failure recorded while the flag is off is still
 * labelled correctly if the flag is later turned on. Cost, stated: one inert extra string per failed
 * order in localStorage while OFF — nothing reads it there.
 *
 * Beta-only (BETA_ONLY_FLAGS in deployAllowlist.test.ts); withheld from production. The production
 * gate is OMISSION from ALLOWED in scripts/deploy-prod-fe.sh — do not add it there without a
 * separate decision, that edit IS the launch.
 */
export function isFailureStepStateEnabled(): boolean {
  return import.meta.env.VITE_FAILURE_STEP_STATE === 'true'
}

/**
 * #261 F8 — cross-chain review-card fee display (the debit that does not reconcile).
 *
 * OFF (default, and today's production behaviour): on a STABLE-source cross-chain swap the card and
 * the review modal both state `Total fees` as the ALL-IN `feesTotalUsd`, which includes the deBridge
 * flat fee the user pays SEPARATELY in the source chain's native token. On the flagship route
 * (`solana USDC → robinhood ETH`, 2.5 USDC) that reads `Total fees ≈ $2.15` against a $2.50 send and
 * a $1.49 receive — a fee that cannot be reconciled with the two numbers beside it. The give row
 * meanwhile states `You pay 2.50 + 1.0069 fee = 3.5069` against a real 2.50 debit.
 *
 * ON (the shipped contract — operator decision DEV-363 ITEM A, SUPERSEDING E8's rename; earlier
 * drafts of this comment described first the C-4/E6 shape (REVERSED before merge) and then E8's
 * rename (REPLACED by DEV-363 — READ THIS ONE, NOT THOSE). THIS IS THE AUTHORITATIVE PROMOTE
 * CONTRACT for this flag; every other file's description of it must agree with this one, not the
 * reverse):
 *
 *   · The fee-total row that used to carry the WEDGE (`feesTotalUsd − fixFeeUsd`,
 *     `2.50 − 1.01 ≈ 1.49`) is WITHHELD, not renamed. E8's `Fees from your receive` relabel is
 *     retired: DEV-363 removes the row on this branch rather than renaming it again. Every other
 *     branch, and every branch flag-OFF, still renders it verbatim as `Total fees`. Nothing on this
 *     branch visibly sums to the all-in any more — see the `Bridge & swap cost` bullet below for what
 *     the screen states instead.
 *   · The separately-paid native flat fee keeps its own `Bridge & swap cost` line with BOTH the
 *     native amount AND its USD parenthetical RESTORED (`0.015 SOL (≈ $1.14)`), annotated
 *     `— paid separately in {symbol}`. Before DEV-363 this line and the (now-withheld) wedge row
 *     visibly summed to the $2.15 all-in on screen; with the wedge row gone, this is the only fee
 *     figure that renders — the $1.01 wedge stays DERIVABLE (2.50 send − 1.49 receive) but is no
 *     longer STATED anywhere (ratified, REVIEW-triage gate 2026-09-07, operator decision O-2a).
 *   · That line is gated on `fixFeeSymbol` (MED-4) — and so is the wedge (G4-1): a wedge is a figure
 *     that EXCLUDES this fee, so it may only render where the fee's own line can. With no symbol
 *     there is no honest denomination, the row is withheld, and the fee-total row falls back to the
 *     all-in that CONTAINS the fee. See `FeeSection/dexTotal.ts`.
 *     THAT GATE IS PART OF "ON", NOT A SIDE-EFFECT OF SHIPPING THE COMMIT (S1 finding F-1). The card's
 *     `Bridge & swap cost` row pre-dates F8, so its symbol precondition carries an explicit flag term
 *     (`FeeSection/index.tsx`); flag OFF that row keeps today's `fixFeeSymbol ?? sourceCurrency`
 *     fallback verbatim, mislabel included. Fixing the mislabel is a change, so it flips with the flag
 *     like every other change here — which is what keeps the OFF paragraph above literally true. It
 *     was not, for one commit: the gate shipped ungated while this comment already promised a no-op,
 *     and the promote decision rests on that promise. Restored by gating, not by hedging the prose.
 *   · The review modal's TO row carries a fiat subtitle so the subtraction is performable on screen.
 *   · The COLLAPSED card label stays the ALL-IN (G2) — the split is what the expanded panel is for.
 *   · The falsified `input + opex = give` "You pay" block (label, tooltip, value AND subtitle) is
 *     WITHHELD under F8-ON, not corrected (DEV-363 ITEM B — this bullet previously described a
 *     correction conditional on F7; the shipped gate carries no F7 term). The whole block disappears
 *     from the modal under F8-ON regardless of F7: `ReviewTransferModal/index.tsx`'s gate is
 *     `isDexRoute && dexGive != null && !feeDisplayOn`, so F8 alone suppresses it everywhere. Flag
 *     OFF renders it byte-identical to today, legacy wording included.
 *
 * DISPLAY ONLY, AND THAT IS LOAD-BEARING (ADDENDUM C, C-7). `computeDexDisplayTotals` has a third
 * caller — `dexFeeUnavailable` (BridgePage.tsx:1788) — which feeds `canSend`. This flag reaches the
 * two DISPLAY call sites and never that one, so `dexFeeUnavailable.blocked` is identical in both flag
 * states. A beta display flag must never change what is submittable.
 *
 * Beta-only (BETA_ONLY_FLAGS in deployAllowlist.test.ts); withheld from production. The production
 * gate is OMISSION from ALLOWED in scripts/deploy-prod-fe.sh — do not add it there without a
 * separate decision, that edit IS the launch.
 */
export function isCrossChainFeeDisplayEnabled(): boolean {
  return import.meta.env.VITE_CROSSCHAIN_FEE_DISPLAY === 'true'
}

/**
 * #261 F3 — the recipient label names the WALLET and the NETWORK.
 *
 * OFF (default, and today's production behaviour): whenever the derived recipient IS the connected
 * wallet, the review card's recipient row renders the literal `(your wallet)` beneath the truncated
 * address (`ReviewTransferModal/index.tsx`), the two `SwapCard` wallet chips render the generic
 * wallet glyph because neither mount passes `walletKind`, and `RecipientModal`'s "Use connected
 * address" option names no provider. On a Robinhood destination that is an inverted reassurance: the
 * connected MetaMask on chain 4663 is NOT the user's Robinhood app balance, which is the #261 live
 * outcome — the RH app showed $0.00 until the user swept manually.
 *
 * ON: that row reads `(MetaMask · Robinhood network)` — wallet AND network (ADDENDUM A1; the network
 * half is the load-bearing one, because chain 4663 is not a default MetaMask network either, so
 * `(MetaMask)` alone would just move the "$0.00 looks like a loss" moment one app to the left). A
 * WalletConnect session reads `via WalletConnect · … network`; an unmapped/`Injected` connector reads
 * `your connected wallet · … network` and never guesses a provider. The SwapCard chips carry the
 * connected wallet's brand icon, with the RECEIVE chip's icon gated on `recipientIsConnectedWallet`
 * so a pasted third-party recipient is never branded as the user's own. Scope is GLOBAL (A2) — every
 * self-bridge, not only Robinhood.
 *
 * DISPLAY ONLY, AND STRUCTURALLY SO (MP-2 / ADDENDUM A3). `utils/recipient.ts` — the derivation that
 * produces the SIGNED recipient — has a zero-line diff in this change, so the rendered string stays
 * byte-identical to `reviewRecipientAddress` in both flag states. The label additionally FAILS CLOSED:
 * an address-shaped label input is refused and falls back to the pre-F3 copy
 * (`utils/walletLabel.ts:isRenderableWalletLabel`), so the row can never grow a second, looser
 * recipient string (#143). Nothing here reaches `canSend`; what is submittable is identical in both
 * states.
 *
 * Two bits ship UNFLAGGED (ADDENDUM A5) because they are additive, display-only and touch no fund
 * path: `WalletChip`'s `aria-label`, and `WalletModal`'s connect-list naming routing through the
 * shared normaliser (behaviour-preserving — see `utils/walletLabel.ts:walletDisplayName`).
 *
 * Beta-only (BETA_ONLY_FLAGS in deployAllowlist.test.ts); withheld from production. The production
 * gate is OMISSION from ALLOWED in scripts/deploy-prod-fe.sh — do not add it there without a
 * separate decision, that edit IS the launch. Per ADDENDUM A6 the OFF state is a live production
 * defect, so the prod-enable is a tracked HOLD, not a resting state.
 */
export function isWalletTypeLabelEnabled(): boolean {
  return import.meta.env.VITE_WALLET_TYPE_LABEL === 'true'
}

/**
 * #261 M3 — the pre-quote receive flash: a NATIVE source mirrored as the DEST token.
 *
 * OFF (default, and today's production behaviour): `estimatedReceivedDetail`'s last forward DEX
 * fallback echoes the SEND amount as the RECEIVE figure whenever the dest is non-native and no live
 * estimate is in hand (`BridgePage.tsx`, the `driver: 'liveAmount'` branch). That branch's own
 * comment states the premise out loud — "fees are small" — and the premise is a SAME-VALUE-PAIR
 * premise: it holds for stable→stable (~1:1, the mirror is within a cent) and collapses for a native
 * source. On ETH → USDG the card renders `0.00041 ETH` as `0.00041 USDG` — ~$0.77 shown as ~$0.0004,
 * about 1870× wrong — in the ordinary green real-quote styling, indistinguishable from a real quote.
 *
 * ON: the mirror requires BOTH ends to be non-native, so it survives only for the pair it was ever
 * true for. A native source with no quote in hand falls through to the `'0.0'` blank that the
 * native-DEST no-estimate case has always returned, one line below — SwapCard then renders `0.00`
 * (non-editable dest) or an empty field showing its `placeholder="0.00"` (editable dest), and the
 * `≈ $X` fiat subtitle self-suppresses on its own `parseFloat(...) > 0` guard.
 *
 * DISPLAY ONLY IN THE SENSE THAT MATTERS — stated as MONOTONICITY, not as equality. An earlier draft
 * of this comment claimed submittability was "identical in both flag states". Review found that too
 * strong (the enumeration of `dex.ts` sites behind it was incomplete), so it is corrected here rather
 * than softened — this paragraph is quoted by the deploy wiring as the basis for shipping dark.
 *
 * THE FLAG CAN ONLY EVER TIGHTEN THE SUBMIT GATE, NEVER LOOSEN IT. The sole change is mirror → '0.0',
 * and `dexReceiveNonPositive` blocks on a non-positive receive, so the flag can only move a state from
 * submittable to blocked. It can never un-block a Send. That is what the F8 invariant needs here: a
 * beta display flag must not make something submittable that was not.
 *
 * In every state the backend CURRENTLY emits the two flag states are in fact identical, because each
 * is already blocked by a gate that never reads the receive figure:
 *   · no estimate in hand (the dominant pre-quote state) → `dexEstimateMissing` (tests
 *     `!dexFeeEstimate`);
 *   · an estimate present but unpriced → `opexGate`, because every site in
 *     `apps/api/src/routes/dex.ts` that leaves `estimatedReceived` null (the `quoteUnavailable` path
 *     at :1940 and the two Tron policy refusals at :915/:934) ships `viability.level: 'block'` or
 *     `routeUnsupported`, and Jupiter's failure path returns 503 rather than a partial estimate.
 * The exception, named because it is what broke the equality claim: `dex.ts:1285` passes the bot's
 * bps-only `/quote` body through verbatim, carrying neither `estimatedReceived` nor a block marker.
 * On a native source that shape reaches the branch and the flag DOES move the gate — toward BLOCKED.
 * It is dead today only by coincidence (the bot reads the same cache row the route just missed), so
 * it is pinned as a test rather than trusted to stay dead.
 *
 * Pinned by `BridgePage.m3PrequoteReceive.test.tsx`: two cases assert `canSend` ON === OFF in the
 * ordinary states, and one asserts the direction (OFF submittable, ON blocked) on the shape above —
 * which is what makes the real property falsifiable instead of merely stated.
 *
 * The `driver` tag is deliberately NOT retagged (#336 R7 / #337). `'liveAmount'` means "this figure
 * IS `bridgeState.amount` echoed", so it stays on the branch that still echoes it; the native-source
 * path returns a CONSTANT and is therefore `'estimate'`, exactly like the native-dest line below it.
 * Retagging would have changed what `dexFee`'s staleness guard is asked, which is a different change.
 *
 * Beta-only (BETA_ONLY_FLAGS in deployAllowlist.test.ts); withheld from production. The production
 * gate is OMISSION from ALLOWED in scripts/deploy-prod-fe.sh — do not add it there without a
 * separate decision, that edit IS the launch. Note the OFF state is a live production defect (the
 * user is shown a wildly wrong receive), so the prod-enable is a tracked HOLD, not a resting state.
 */
export function isPrequoteReceiveBlankEnabled(): boolean {
  return import.meta.env.VITE_PREQUOTE_RECEIVE_BLANK === 'true'
}

/**
 * DEV-363 ITEM C — the 1Click deposit-address notice on the review modal.
 *
 * OFF (default, and today's production behaviour): the review modal renders exactly what it renders
 * today, byte-identically. Nothing is added, and no route behaves differently.
 *
 * ON: on a `near_1click` route, with a resolved `recipientAddress`, the modal adds ONE line — raised
 * to `fontSize: 13` to match the trust panel below it, the one line whose whole purpose is to speak
 * before the wallet does — ABOVE the Recipient row stating:
 *
 *   "This transfer is first sent to a deposit address created for this order, so your wallet may
 *   show it as a new address."
 *
 * This is the operator's own clause (Jira DEV-363 comment 10289), not a paraphrase, and it EXPLAINS
 * WHY the wallet will flag the destination as new — the line now literally does what this docblock
 * always promised it would. The reason it exists: 1Click settles by having the user send to a freshly
 * generated deposit address, so the wallet sees a destination it has never seen before and flags it
 * as new. Today the WALLET is the first thing that tells the user that, which reads as an alarm about
 * the transfer rather than as a description of how the rail works — so we say it first, in our own
 * screen, positioned above the Recipient row rather than beneath Parse's own fraud warning so it
 * cannot be misread as an explanation FOR that warning.
 *
 * WHAT THE LINE MAY SAY IS CONSTRAINED, AND THE CONSTRAINTS ARE ASSERTED IN TESTS, NOT TRUSTED:
 *   · it EXPLAINS and never INSTRUCTS. It may not contain `ignore`, `dismiss`, `safely`, `proceed`,
 *     `it's fine` or `don't worry`. Telling a user to click past a wallet warning is worse than the
 *     warning, and it is the one thing this line must never grow into.
 *   · it may not say `single-use` OR claim "no prior activity" — the same unverified assertion in
 *     different words. NEAR's live docs (fetched 2026-09-07) document *"a unique deposit address"*
 *     per quote but do NOT document single-use or an absence of prior activity, so the stronger claim
 *     is UNVERIFIED and cannot be asserted on a money screen. `created for this order` is the
 *     verified claim. Dropping "no prior activity" also happens to dissolve a memo-corridor hazard: a
 *     shared memo-chain deposit address WOULD have prior activity, but "your wallet may show it as a
 *     new address" holds true either way. (That memo behaviour is a separate, filed issue — GH #587 /
 *     DEV-406 — not this line's to fix.)
 *   · it DOES mention the wallet, positively asserted — the operator's own clause is the point of the
 *     line, not an incidental detail a reword could drop while still passing every negative check.
 *   · it is PROSPECTIVE. The deposit address does not exist at review time — it arrives as
 *     `dexBridgeResponse?.depositAddress` after order creation — so the line cannot quote or link one.
 *
 * THIRD GATE: `recipientAddress`. Added alongside the reposition so the line can never render while
 * the Recipient row it now sits above still reads "Not set" — this line asserts something about
 * where funds are going, and there is nowhere resolved yet to assert it about.
 *
 * DISPLAY ONLY, AND THAT IS STRUCTURAL. This flag is read at exactly one render site, whose whole
 * output is one `<div>` of text. It reaches no estimate, no resolver, no `canSend` input and no
 * signed payload, so it cannot move what is SUBMITTABLE in either state.
 *
 * Beta-only (BETA_ONLY_FLAGS in deployAllowlist.test.ts); withheld from production. The production
 * gate is OMISSION from ALLOWED in scripts/deploy-prod-fe.sh — do not add it there without a
 * separate decision, that edit IS the launch.
 */
export function isDepositAddressNoticeEnabled(): boolean {
  return import.meta.env.VITE_DEPOSIT_ADDRESS_NOTICE === 'true'
}
